import { useState } from "react";
import {
  Users,
  Briefcase,
  Clock,
  CheckCircle2,
  XCircle,
  TrendingUp,
  TrendingDown,
  Minus,
  AlertTriangle,
} from "lucide-react";
import type { ColDef } from "@/components/admin/shared/AdminTable";
import { AdminTable } from "@/components/admin/shared/AdminTable";
import { PageHeader } from "@/components/admin/shared/PageHeader";
import PennText from "@/components/ui/text/PennText";

// ── Types ─────────────────────────────────────────────────────────────────────

type CardId =
  | "total-users"
  | "active-users"
  | "total-applications"
  | "pending"
  | "approved"
  | "rejected";

type TrendType = "up" | "down" | "neutral" | "alert";

type StatCardDef = {
  id: CardId;
  label: string;
  value: string;
  trend: string;
  trendType: TrendType;
  icon: React.ReactNode;
  iconBg: string;
  iconColor: string;
  accentBorder: string;
  accentRing: string;
  accentBg: string;
};

type AnyRow = Record<string, string | number | boolean>;

type TableConfig = {
  title: string;
  emptyMessage?: string;
  columns: ColDef<AnyRow>[];
  rows: AnyRow[];
  keyExtractor: (row: AnyRow) => string | number;
};

// ── Stat card definitions (matching screenshot) ───────────────────────────────

const CARDS: StatCardDef[] = [
  {
    id: "total-users",
    label: "Total Users",
    value: "12,482",
    trend: "+12%",
    trendType: "up",
    icon: <Users size={18} />,
    iconBg: "bg-penn-50 dark:bg-penn-900/40",
    iconColor: "text-penn-500",
    accentBorder: "border-b-penn-500",
    accentRing: "ring-penn-500/30",
    accentBg: "bg-penn-50/40 dark:bg-penn-950/20",
  },
  {
    id: "active-users",
    label: "Active Users",
    value: "8,941",
    trend: "+5%",
    trendType: "up",
    icon: <Users size={18} />,
    iconBg: "bg-penn-50 dark:bg-penn-900/40",
    iconColor: "text-penn-500",
    accentBorder: "border-b-penn-500",
    accentRing: "ring-penn-500/30",
    accentBg: "bg-penn-50/40 dark:bg-penn-950/20",
  },
  {
    id: "total-applications",
    label: "Total Applications",
    value: "4,210",
    trend: "0%",
    trendType: "neutral",
    icon: <Briefcase size={18} />,
    iconBg: "bg-penn-50 dark:bg-penn-900/40",
    iconColor: "text-penn-500",
    accentBorder: "border-b-penn-500",
    accentRing: "ring-penn-500/30",
    accentBg: "bg-penn-50/40 dark:bg-penn-950/20",
  },
  {
    id: "pending",
    label: "Pending",
    value: "158",
    trend: "24h",
    trendType: "alert",
    icon: <Clock size={18} />,
    iconBg: "bg-orange-50 dark:bg-orange-950/40",
    iconColor: "text-orange-500",
    accentBorder: "border-b-orange-500",
    accentRing: "ring-orange-500/30",
    accentBg: "bg-orange-50/30 dark:bg-orange-950/20",
  },
  {
    id: "approved",
    label: "Approved",
    value: "3,842",
    trend: "+18%",
    trendType: "up",
    icon: <CheckCircle2 size={18} />,
    iconBg: "bg-green-50 dark:bg-green-950/40",
    iconColor: "text-green-500",
    accentBorder: "border-b-green-500",
    accentRing: "ring-green-500/30",
    accentBg: "bg-green-50/30 dark:bg-green-950/20",
  },
  {
    id: "rejected",
    label: "Rejected",
    value: "210",
    trend: "-2%",
    trendType: "down",
    icon: <XCircle size={18} />,
    iconBg: "bg-red-50 dark:bg-red-950/40",
    iconColor: "text-red-500",
    accentBorder: "border-b-red-500",
    accentRing: "ring-red-500/30",
    accentBg: "bg-red-50/30 dark:bg-red-950/20",
  },
];

// ── Mock table data per card ───────────────────────────────────────────────────

const USER_COLS: ColDef<AnyRow>[] = [
  { header: "Name", key: "Name" },
  { header: "Email", key: "Email" },
  { header: "Company", key: "Company" },
  { header: "Role", key: "Role" },
  { header: "Region", key: "Region" },
  {
    header: "Status",
    render: (r) => (
      <span className={`px-2 py-0.5 text-small-medium rounded-lg border ${r["status"] === "Active" ? "bg-green-50 text-green-700 border-green-300" : "bg-gray-100 text-blue_grey3 border-gray-300"}`}>
        {r["status"] as string}
      </span>
    ),
  },
];

const ALL_USERS: AnyRow[] = [
  { id: 1, "Name": "Andrew Robins", "Email": "ARobins@rittal-csm.co.uk", "Company": "Rittal-CSM Ltd", "Role": "Production Planning", "Region": "EU", "status": "Active" },
  { id: 2, "Name": "Dariusz Rosinski", "Email": "dariusz.rosinski@flex.com", "Company": "Flextronics Poland", "Role": "Production Planning", "Region": "EU", "status": "Active" },
  { id: 3, "Name": "Anurag Rajput", "Email": "engineer1@gmail.com", "Company": "BYD", "Role": "Design / Engineering", "Region": "ASIA", "status": "Active" },
  { id: 4, "Name": "Jose Ceron", "Email": "jose.ceron@flex.com", "Company": "Flextronics Mexico", "Role": "Production", "Region": "NA", "status": "Inactive" },
  { id: 5, "Name": "Lars Dressler", "Email": "ldressler@haeger.com", "Company": "PennEngineering", "Role": "Management", "Region": "EU", "status": "Active" },
  { id: 6, "Name": "Manager Rajput", "Email": "manager@gmail.com", "Company": "BYD", "Role": "Management", "Region": "ASIA", "status": "Active" },
  { id: 7, "Name": "Test Customer", "Email": "test@customer.com", "Company": "Testcompany", "Role": "Internal Sales", "Region": "NA", "status": "Inactive" },
];

const TASK_COLS: ColDef<AnyRow>[] = [
  { header: "Task ID", key: "Task ID" },
  { header: "Requested By", key: "Requested By" },
  { header: "Company", key: "Company" },
  { header: "Machine", key: "Machine" },
  { header: "Created", key: "Created" },
  {
    header: "Status",
    render: (r) => {
      const s = r["Status"] as string;
      const styles: Record<string, string> = {
        PENDING: "bg-orange-50 text-orange-700 border-orange-300",
        APPROVED: "bg-green-50 text-green-700 border-green-300",
        REJECTED: "bg-red-50 text-red-700 border-red-300",
        OPEN: "bg-penn-50 text-penn-700 border-penn-400",
      };
      return (
        <span className={`px-2 py-0.5 text-small-medium rounded-lg border ${styles[s] ?? "bg-gray-100 text-blue_grey3 border-gray-300"}`}>
          {s}
        </span>
      );
    },
  },
];

const PENDING_ROWS: AnyRow[] = [
  { id: "p1", "Task ID": "TASK-104", "Requested By": "andrew@rittal.com", "Company": "Rittal-CSM", "Machine": "618 Pro", "Created": "2026-05-24 09:12", "Status": "PENDING" },
  { id: "p2", "Task ID": "TASK-107", "Requested By": "dariusz@flex.com", "Company": "Flextronics", "Machine": "N/A", "Created": "2026-05-24 14:33", "Status": "PENDING" },
  { id: "p3", "Task ID": "TASK-109", "Requested By": "jose@flex.com", "Company": "Flextronics Mexico", "Machine": "PEMSerter", "Created": "2026-05-25 08:01", "Status": "PENDING" },
  { id: "p4", "Task ID": "TASK-112", "Requested By": "engineer1@gmail.com", "Company": "BYD", "Machine": "616 Pro", "Created": "2026-05-25 11:45", "Status": "PENDING" },
  { id: "p5", "Task ID": "TASK-115", "Requested By": "test@customer.com", "Company": "Testcompany", "Machine": "N/A", "Created": "2026-05-26 07:30", "Status": "PENDING" },
];

const APPROVED_ROWS: AnyRow[] = [
  { id: "a1", "Task ID": "TASK-088", "Requested By": "andrew@rittal.com", "Company": "Rittal-CSM", "Machine": "618 Pro", "Created": "2026-05-20 10:00", "Status": "APPROVED" },
  { id: "a2", "Task ID": "TASK-091", "Requested By": "lars@haeger.com", "Company": "PennEngineering", "Machine": "PEMSerter", "Created": "2026-05-21 14:22", "Status": "APPROVED" },
  { id: "a3", "Task ID": "TASK-094", "Requested By": "manager@gmail.com", "Company": "BYD", "Machine": "616 Pro", "Created": "2026-05-22 09:10", "Status": "APPROVED" },
  { id: "a4", "Task ID": "TASK-098", "Requested By": "dariusz@flex.com", "Company": "Flextronics", "Machine": "618 Pro", "Created": "2026-05-23 15:45", "Status": "APPROVED" },
  { id: "a5", "Task ID": "TASK-101", "Requested By": "engineer1@gmail.com", "Company": "BYD", "Machine": "N/A", "Created": "2026-05-23 16:00", "Status": "APPROVED" },
];

const REJECTED_ROWS: AnyRow[] = [
  { id: "r1", "Task ID": "TASK-082", "Requested By": "test@customer.com", "Company": "Testcompany", "Machine": "N/A", "Created": "2026-05-18 08:55", "Status": "REJECTED" },
  { id: "r2", "Task ID": "TASK-085", "Requested By": "jose@flex.com", "Company": "Flextronics Mexico", "Machine": "616 Pro", "Created": "2026-05-19 13:20", "Status": "REJECTED" },
  { id: "r3", "Task ID": "TASK-087", "Requested By": "andrew@rittal.com", "Company": "Rittal-CSM", "Machine": "PEMSerter", "Created": "2026-05-20 09:00", "Status": "REJECTED" },
];

const APP_COLS: ColDef<AnyRow>[] = [
  { header: "Application ID", key: "Application ID" },
  { header: "Name", key: "Name" },
  { header: "Type", key: "Type" },
  { header: "Region", key: "Region" },
  { header: "Machine Match", key: "Machine Match" },
  { header: "Created", key: "Created" },
];

const APP_ROWS: AnyRow[] = [
  { id: "app1", "Application ID": "APP-001", "Name": "Auto Assembly Line A", "Type": "Assembly", "Region": "EU", "Machine Match": "618 Pro", "Created": "2026-05-10" },
  { id: "app2", "Application ID": "APP-002", "Name": "PCB Insertion Line", "Type": "Electronics", "Region": "ASIA", "Machine Match": "PEMSerter", "Created": "2026-05-12" },
  { id: "app3", "Application ID": "APP-003", "Name": "Sheet Metal Fastening", "Type": "Fabrication", "Region": "NA", "Machine Match": "616 Pro", "Created": "2026-05-14" },
  { id: "app4", "Application ID": "APP-004", "Name": "Enclosure Assembly", "Type": "Enclosure", "Region": "EU", "Machine Match": "618 Pro", "Created": "2026-05-15" },
  { id: "app5", "Application ID": "APP-005", "Name": "EV Battery Tray", "Type": "Automotive", "Region": "ASIA", "Machine Match": "616 Pro", "Created": "2026-05-18" },
  { id: "app6", "Application ID": "APP-006", "Name": "Control Panel", "Type": "Electronics", "Region": "NA", "Machine Match": "PEMSerter", "Created": "2026-05-20" },
];

const TABLE_CONFIGS: Record<CardId, TableConfig> = {
  "total-users": {
    title: "All Users",
    emptyMessage: "No users found.",
    columns: USER_COLS,
    rows: ALL_USERS,
    keyExtractor: (r) => r["id"] as number,
  },
  "active-users": {
    title: "Active Users",
    emptyMessage: "No active users found.",
    columns: USER_COLS,
    rows: ALL_USERS.filter((r) => r["status"] === "Active"),
    keyExtractor: (r) => r["id"] as number,
  },
  "total-applications": {
    title: "All Applications",
    emptyMessage: "No applications found.",
    columns: APP_COLS,
    rows: APP_ROWS,
    keyExtractor: (r) => r["id"] as string,
  },
  pending: {
    title: "Pending Tasks",
    emptyMessage: "No pending tasks.",
    columns: TASK_COLS,
    rows: PENDING_ROWS,
    keyExtractor: (r) => r["id"] as string,
  },
  approved: {
    title: "Approved Tasks",
    emptyMessage: "No approved tasks.",
    columns: TASK_COLS,
    rows: APPROVED_ROWS,
    keyExtractor: (r) => r["id"] as string,
  },
  rejected: {
    title: "Rejected Tasks",
    emptyMessage: "No rejected tasks.",
    columns: TASK_COLS,
    rows: REJECTED_ROWS,
    keyExtractor: (r) => r["id"] as string,
  },
};

// ── Trend badge ───────────────────────────────────────────────────────────────

const TrendBadge = ({ trend, trendType }: { trend: string; trendType: TrendType }) => {
  if (trendType === "up") {
    return (
      <span className="inline-flex items-center gap-1 text-caption font-medium text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-950/40 border border-green-200 dark:border-green-800 rounded-full px-2 py-0.5">
        <TrendingUp size={10} />
        {trend}
      </span>
    );
  }
  if (trendType === "down") {
    return (
      <span className="inline-flex items-center gap-1 text-caption font-medium text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-800 rounded-full px-2 py-0.5">
        <TrendingDown size={10} />
        {trend}
      </span>
    );
  }
  if (trendType === "alert") {
    return (
      <span className="inline-flex items-center gap-1 text-caption font-medium text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-950/40 border border-orange-200 dark:border-orange-800 rounded-full px-2 py-0.5">
        <AlertTriangle size={10} />
        {trend}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-caption font-medium text-blue_grey3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-full px-2 py-0.5">
      <Minus size={10} />
      {trend}
    </span>
  );
};

// ── Stat card ─────────────────────────────────────────────────────────────────

const StatCard = ({
  card,
  selected,
  onClick,
}: {
  card: StatCardDef;
  selected: boolean;
  onClick: () => void;
}) => (
  <button
    onClick={onClick}
    className={[
      "group relative w-full text-left p-4 rounded-2xl border transition-all duration-200 cursor-pointer",
      "bg-white dark:bg-gray-900",
      selected
        ? `border-b-2 ${card.accentBorder} border-x-gray-200 border-t-gray-200 dark:border-x-gray-800 dark:border-t-gray-800 shadow-md ring-1 ${card.accentRing} ${card.accentBg}`
        : "border-gray-200 dark:border-gray-800 shadow-sm hover:shadow-md hover:border-gray-300 dark:hover:border-gray-700",
    ].join(" ")}
  >
    {/* Top row: icon + trend */}
    <div className="flex items-start justify-between mb-3">
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${card.iconBg} ${card.iconColor} transition-colors`}>
        {card.icon}
      </div>
      <TrendBadge trend={card.trend} trendType={card.trendType} />
    </div>

    {/* Label */}
    <p className="text-label uppercase text-blue_grey4 mb-1 truncate">
      {card.label}
    </p>

    {/* Value */}
    <p className={`text-h2 ${selected ? card.iconColor : "text-blue_grey1 dark:text-white"} transition-colors`}>
      {card.value}
    </p>

    {/* Selected indicator dot */}
    {selected && (
      <span className={`absolute bottom-3 right-3 w-1.5 h-1.5 rounded-full ${card.iconColor.replace("text-", "bg-")} opacity-70`} />
    )}
  </button>
);

// ── Main page ─────────────────────────────────────────────────────────────────

export const OverviewPage = () => {
  const [selectedCard, setSelectedCard] = useState<CardId>("total-users");
  const [loading, setLoading] = useState(false);

  const handleCardClick = (id: CardId) => {
    if (id === selectedCard) return;
    setLoading(true);
    setSelectedCard(id);
    // Brief loading state for perceived responsiveness
    setTimeout(() => setLoading(false), 350);
  };

  const config = TABLE_CONFIGS[selectedCard];
  const activeCard = CARDS.find((c) => c.id === selectedCard)!;

  return (
    <>
      <PageHeader
        title="Dashboard Overview"
        subtitle="Platform activity and key metrics at a glance."
      />

      {/* ── Stat cards ── */}
      <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-3 mb-7">
        {CARDS.map((card) => (
          <StatCard
            key={card.id}
            card={card}
            selected={card.id === selectedCard}
            onClick={() => handleCardClick(card.id)}
          />
        ))}
      </div>

      {/* ── Table section ── */}
      <div>
        {/* Table header */}
        <div className="flex items-center gap-3 mb-3">
          <div className={`w-6 h-6 rounded-lg flex items-center justify-center ${activeCard.iconBg} ${activeCard.iconColor}`}>
            {activeCard.icon}
          </div>
          <PennText as="h2" size="lg" font="semibold" color="on-navy">
            {config.title}
          </PennText>
          <span className="inline-flex items-center px-2 py-0.5 text-small-medium bg-gray-100 dark:bg-gray-800 text-blue_grey3 rounded-full border border-gray-200 dark:border-gray-700">
            {config.rows.length}
          </span>
        </div>

        <AdminTable
          columns={config.columns}
          rows={config.rows}
          keyExtractor={config.keyExtractor}
          loading={loading}
          emptyMessage={config.emptyMessage}
        />
      </div>
    </>
  );
};
